import React, { useState } from 'react';
import aiBrainLogo from '../../../assets/ai-brain-actual.png';

// Types from our documented flow
interface LawFirmSignupData {
  // Firm Information
  firmName: string;
  firmId: string; // User-defined firm ID for Keycloak realm
  practiceAreas: string[];
  firmSize: string;
  
  // Admin User Information  
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone?: string;
  
  // Firm Settings
  timezone: string;
  preferredLanguage: string;
  
  // Legal/Compliance
  termsAccepted: boolean;
  privacyPolicyAccepted: boolean;
}

interface SignupResponse {
  success: boolean;
  realmName: string;
  message: string;
  nextSteps: {
    loginUrl: string;
    adminEmail: string;
    checkEmail: boolean;
  };
}

const PRACTICE_AREAS = [
  'Personal Injury',
  'Corporate Litigation',
  'Family Law',
  'Criminal Law',
  'Real Estate',
  'Employment Law',
  'Intellectual Property',
  'Estate Planning',
  'Immigration Law',
  'Tax Law',
  'Civil Litigation',
  'Business Formation / Compliance',
  'Other'
];

const FIRM_SIZES = [
  '1-5 attorneys',
  '6-15 attorneys', 
  '16-50 attorneys',
  '51-200 attorneys',
  '200+ attorneys'
];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago', 
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu'
];

/**
 * LawFirmSignupForm - Complete law firm onboarding form
 * Implements the EdgeLex SaaS signup flow from www.webelievesoftware.com/signup
 */
const LawFirmSignupForm: React.FC = () => {
  
  const [formData, setFormData] = useState<LawFirmSignupData>({
    firmName: '',
    firmId: '',
    practiceAreas: [],
    firmSize: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    timezone: 'America/New_York',
    preferredLanguage: 'en',
    termsAccepted: false,
    privacyPolicyAccepted: false
  });
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [signupComplete, setSignupComplete] = useState(false);
  const [signupResponse, setSignupResponse] = useState<SignupResponse | null>(null);

  // Form validation
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    switch (step) {
      case 1: // Firm Information
        if (!formData.firmName.trim()) newErrors.firmName = 'Firm name is required';
        if (!formData.firmId.trim()) {
          newErrors.firmId = 'Firm ID is required';
        } else if (!/^[a-z0-9-]{3,30}$/.test(formData.firmId)) {
          newErrors.firmId = 'Firm ID must be 3-30 characters, lowercase letters, numbers, and hyphens only';
        }
        if (formData.practiceAreas.length === 0) newErrors.practiceAreas = 'Select at least one practice area';
        if (!formData.firmSize) newErrors.firmSize = 'Firm size is required';
        break;
        
      case 2: // Admin Information
        if (!formData.adminFirstName.trim()) newErrors.adminFirstName = 'First name is required';
        if (!formData.adminLastName.trim()) newErrors.adminLastName = 'Last name is required';
        if (!formData.adminEmail.trim()) newErrors.adminEmail = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
          newErrors.adminEmail = 'Valid email is required';
        }
        break;
        
      case 3: // Terms & Settings
        if (!formData.termsAccepted) newErrors.termsAccepted = 'You must accept the terms of service';
        if (!formData.privacyPolicyAccepted) newErrors.privacyPolicyAccepted = 'You must accept the privacy policy';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Auto-generate firm ID suggestion when firm name changes
  const generateFirmIdSuggestion = (firmName: string): string => {
    return firmName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
      .substring(0, 25); // Limit length
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error(`Signup failed: ${response.statusText}`);
      }
      
      const result: SignupResponse = await response.json();
      setSignupResponse(result);
      setSignupComplete(true);
      
    } catch (error) {
      console.error('Signup error:', error);
      setErrors({ submit: error instanceof Error ? error.message : 'Signup failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Handle field updates with auto-suggestion for firmId
  const updateField = (field: keyof LawFirmSignupData, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-suggest firm ID when firm name changes (only if firmId is empty)
      if (field === 'firmName' && !prev.firmId) {
        updated.firmId = generateFirmIdSuggestion(value);
      }
      
      return updated;
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle practice area selection
  const togglePracticeArea = (area: string) => {
    const updatedAreas = formData.practiceAreas.includes(area)
      ? formData.practiceAreas.filter(a => a !== area)
      : [...formData.practiceAreas, area];
    updateField('practiceAreas', updatedAreas);
  };

  // Success screen with beautiful design
  if (signupComplete && signupResponse) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-lex-accent-hover/20 to-slate-900 flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
        <div className="absolute top-20 left-20 w-72 h-72 bg-lex-accent/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <div className="relative max-w-2xl w-full bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg ring-4 ring-green-400/30 overflow-hidden">
              <img 
                src={aiBrainLogo} 
                alt="EdgeLex AI Brain Logo" 
                className="w-full h-full object-cover"
              />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome to EdgeLex! 🎉
            </h1>
            <p className="text-lex-accent/70">
              Your law firm account has been created successfully
            </p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6 border border-white/20">
            <h2 className="text-lg font-semibold text-white mb-4">Next Steps:</h2>
            <ol className="text-left text-lex-accent/80 space-y-3">
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-lex-accent text-lex-accent-contrast rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <span>Check your email ({signupResponse.nextSteps.adminEmail}) for login instructions</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-lex-accent text-lex-accent-contrast rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <span>Click the login link in your email to access EdgeLex</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-lex-accent text-lex-accent-contrast rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <span>Set up your new password (temporary password provided in email)</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-lex-accent text-lex-accent-contrast rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <span>Start adding your team members and configure your firm settings</span>
              </li>
            </ol>
          </div>
          
          <div className="space-y-4">
            <button
              onClick={() => window.location.href = 'https://www.webelievesoftware.com'}
              className="w-full py-3 px-6 bg-gradient-to-r from-lex-accent to-teal-500 hover:from-lex-accent-hover hover:to-teal-600 text-white font-semibold rounded-lg shadow-lg transition-all transform hover:scale-105"
            >
              Return to EdgeLex Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Multi-step form with beautiful glassmorphism design
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-lex-accent-hover/20 to-slate-900 flex items-center justify-center px-4 py-8">
      <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
      <div className="absolute top-20 left-20 w-72 h-72 bg-lex-accent/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      <div className="relative max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg ring-4 ring-lex-accent/30 overflow-hidden">
            <img 
              src={aiBrainLogo} 
              alt="EdgeLex AI Brain Logo" 
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Join EdgeLex
          </h1>
          <p className="text-lex-accent/70">
            AI Solutions for Legal - Get started with your law firm
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step <= currentStep 
                    ? 'bg-gradient-to-r from-lex-accent to-teal-500 text-white shadow-lg' 
                    : 'bg-white/20 text-lex-accent/70 backdrop-blur-sm'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-4 rounded-full transition-all ${
                    step < currentStep 
                      ? 'bg-gradient-to-r from-lex-accent to-teal-500' 
                      : 'bg-white/20'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-3 text-sm text-lex-accent/70">
            <span>Firm Info</span>
            <span>Admin Details</span>
            <span>Finish Setup</span>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8">
          {/* Step 1: Firm Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">
                Tell us about your law firm
              </h2>
              
              {/* Firm Name */}
              <div>
                <label className="block text-sm font-medium text-lex-accent/80 mb-2">
                  Law Firm Name *
                </label>
                <input
                  type="text"
                  value={formData.firmName}
                  onChange={(e) => updateField('firmName', e.target.value)}
                  className={`w-full px-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-lex-accent/50 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-all ${
                    errors.firmName ? 'border-red-400' : 'border-white/20'
                  }`}
                  placeholder="e.g., Smith & Associates Legal"
                />
                {errors.firmName && <p className="mt-1 text-sm text-red-400">{errors.firmName}</p>}
              </div>

              {/* Firm ID */}
              <div>
                <label className="block text-sm font-medium text-lex-accent/80 mb-2">
                  Law Firm ID * <span className="text-lex-accent/60 text-xs">(Your unique login identifier)</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.firmId}
                    onChange={(e) => updateField('firmId', e.target.value.toLowerCase())}
                    className={`w-full px-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-lex-accent/50 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-all ${
                      errors.firmId ? 'border-red-400' : 'border-white/20'
                    }`}
                    placeholder="e.g., smith-associates"
                    maxLength={30}
                  />
                </div>
                {errors.firmId && <p className="mt-1 text-sm text-red-400">{errors.firmId}</p>}
                <p className="mt-1 text-xs text-lex-accent/60">
                  <strong>Important:</strong> This ID will be used for login and your email subdomain. Example: "smith-law" → login with "smith-law" and emails at user@smith-law.webelievesoftware.com
                </p>
                {formData.firmId && (
                  <div className="mt-2 p-2 bg-lex-accent/20 border border-lex-accent/30 rounded-lg">
                    <p className="text-xs text-lex-accent/70">
                      <strong>Your Login ID:</strong> <span className="text-white font-mono">{formData.firmId}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Practice Areas */}
              <div>
                <label className="block text-sm font-medium text-lex-accent/80 mb-3">
                  Practice Areas * (select all that apply)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {PRACTICE_AREAS.map((area) => (
                    <label key={area} className="flex items-center space-x-2 p-3 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-all border border-white/10 hover:border-white/20">
                      <input
                        type="checkbox"
                        checked={formData.practiceAreas.includes(area)}
                        onChange={() => togglePracticeArea(area)}
                        className="text-lex-accent focus:ring-lex-accent rounded"
                      />
                      <span className="text-sm text-lex-accent/80">{area}</span>
                    </label>
                  ))}
                </div>
                {errors.practiceAreas && <p className="mt-2 text-sm text-red-400">{errors.practiceAreas}</p>}
              </div>

              {/* Firm Size */}
              <div>
                <label className="block text-sm font-medium text-lex-accent/80 mb-2">
                  Firm Size *
                </label>
                <select
                  value={formData.firmSize}
                  onChange={(e) => updateField('firmSize', e.target.value)}
                  className={`w-full px-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-all ${
                    errors.firmSize ? 'border-red-400' : 'border-white/20'
                  }`}
                >
                  <option value="" className="bg-slate-800 text-white">Select firm size</option>
                  {FIRM_SIZES.map((size) => (
                    <option key={size} value={size} className="bg-slate-800 text-white">{size}</option>
                  ))}
                </select>
                {errors.firmSize && <p className="mt-1 text-sm text-red-400">{errors.firmSize}</p>}
              </div>
            </div>
          )}

          {/* Step 2: Admin Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">
                Administrator Account Details
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-lex-accent/80 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.adminFirstName}
                    onChange={(e) => updateField('adminFirstName', e.target.value)}
                    className={`w-full px-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-lex-accent/50 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-all ${
                      errors.adminFirstName ? 'border-red-400' : 'border-white/20'
                    }`}
                    placeholder="John"
                  />
                  {errors.adminFirstName && <p className="mt-1 text-sm text-red-400">{errors.adminFirstName}</p>}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-lex-accent/80 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.adminLastName}
                    onChange={(e) => updateField('adminLastName', e.target.value)}
                    className={`w-full px-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-lex-accent/50 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-all ${
                      errors.adminLastName ? 'border-red-400' : 'border-white/20'
                    }`}
                    placeholder="Smith"
                  />
                  {errors.adminLastName && <p className="mt-1 text-sm text-red-400">{errors.adminLastName}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-lex-accent/80 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => updateField('adminEmail', e.target.value)}
                  className={`w-full px-4 py-3 bg-white/10 border backdrop-blur-sm rounded-lg text-white placeholder-lex-accent/50 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-all ${
                    errors.adminEmail ? 'border-red-400' : 'border-white/20'
                  }`}
                  placeholder="john@smithfirm.com"
                />
                {errors.adminEmail && <p className="mt-1 text-sm text-red-400">{errors.adminEmail}</p>}
                <p className="mt-1 text-sm text-lex-accent/60">
                  This will be your login email for EdgeLex
                </p>
              </div>

              {/* Phone (Optional) */}
              <div>
                <label className="block text-sm font-medium text-lex-accent/80 mb-2">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  value={formData.adminPhone}
                  onChange={(e) => updateField('adminPhone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 backdrop-blur-sm rounded-lg text-white placeholder-lex-accent/50 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-all"
                  placeholder="+1-555-123-4567"
                />
              </div>

              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-lex-accent/80 mb-2">
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => updateField('timezone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 backdrop-blur-sm rounded-lg text-white focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-all"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz} className="bg-slate-800 text-white">{tz.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 3: Terms & Finish */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">
                Review & Accept Terms
              </h2>
              
              {/* Summary */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                <h3 className="font-semibold text-white mb-4">Account Summary:</h3>
                <div className="space-y-2 text-sm text-lex-accent/80">
                  <p><strong className="text-white">Firm Name:</strong> {formData.firmName}</p>
                  <p><strong className="text-white">Practice Areas:</strong> {formData.practiceAreas.join(', ')}</p>
                  <p><strong className="text-white">Firm Size:</strong> {formData.firmSize}</p>
                  <p><strong className="text-white">Administrator:</strong> {formData.adminFirstName} {formData.adminLastName}</p>
                  <p><strong className="text-white">Email:</strong> {formData.adminEmail}</p>
                </div>
              </div>

              {/* Terms */}
              <div className="space-y-4">
                <label className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.termsAccepted}
                    onChange={(e) => updateField('termsAccepted', e.target.checked)}
                    className="mt-1 text-lex-accent focus:ring-lex-accent rounded"
                  />
                  <span className="text-sm text-lex-accent/80">
                    I accept the <a href="/terms" target="_blank" className="text-lex-accent hover:text-lex-accent-hover underline">Terms of Service</a> *
                  </span>
                </label>
                {errors.termsAccepted && <p className="text-sm text-red-400">{errors.termsAccepted}</p>}

                <label className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.privacyPolicyAccepted}
                    onChange={(e) => updateField('privacyPolicyAccepted', e.target.checked)}
                    className="mt-1 text-lex-accent focus:ring-lex-accent rounded"
                  />
                  <span className="text-sm text-lex-accent/80">
                    I accept the <a href="/privacy" target="_blank" className="text-lex-accent hover:text-lex-accent-hover underline">Privacy Policy</a> *
                  </span>
                </label>
                {errors.privacyPolicyAccepted && <p className="text-sm text-red-400">{errors.privacyPolicyAccepted}</p>}
              </div>

              {errors.submit && (
                <div className="bg-red-500/20 border border-red-400/50 rounded-lg p-4 backdrop-blur-sm">
                  <p className="text-red-300">{errors.submit}</p>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/20">
            <button
              onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
              disabled={currentStep === 1}
              className="px-6 py-2 text-lex-accent/70 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            
            {currentStep < 3 ? (
              <button
                onClick={() => {
                  if (validateStep(currentStep)) {
                    setCurrentStep(prev => prev + 1);
                  }
                }}
                className="px-6 py-3 bg-gradient-to-r from-lex-accent to-teal-500 hover:from-lex-accent-hover hover:to-teal-600 text-white font-semibold rounded-lg shadow-lg transition-all transform hover:scale-105"
              >
                Next Step
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-green-400 disabled:to-emerald-400 text-white font-semibold rounded-lg shadow-lg transition-all transform hover:scale-105 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
              >
                {loading && (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {loading ? 'Creating Account...' : 'Create EdgeLex Account'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LawFirmSignupForm; 